import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { IconLeft, IconPlayArrow, IconRefresh } from '@arco-design/web-react/icon';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { upstreamLabApi } from '@/api/upstreamLab';
import type {
  CatalogOperation,
  CatalogResponse,
  LabRun,
  LabSession,
  LabWebhookItem,
} from '@/types/upstreamLab';

const HTTP_RESULT_COLOR: Record<string, string> = {
  success: 'green',
  upstream_fail: 'orange',
  timeout: 'red',
  error: 'red',
};

function OperationForm({
  operation,
  artifacts,
  isProduction,
  onExecute,
  executing,
}: {
  operation: CatalogOperation;
  artifacts: Record<string, unknown>;
  isProduction: boolean;
  onExecute: (params: Record<string, unknown>, confirm: boolean) => void;
  executing: boolean;
}) {
  const [form] = Form.useForm();
  const [confirmProd, setConfirmProd] = useState(false);
  const isWriteOp = !['sign.verify_sample', 'virtualcard.listcardbins', 'virtualcard.getBalance',
    'virtualcard.retrieve', 'virtualcard.transaction', 'virtualcard.listrecharges',
    'virtualcard.listwriteoffs', 'virtualcard.listriskcards'].includes(operation.id);

  useEffect(() => {
    const initial: Record<string, unknown> = {};
    for (const field of operation.fields) {
      if (field.bindArtifact && artifacts[field.bindArtifact] != null) {
        initial[field.name] = artifacts[field.bindArtifact];
      } else if (field.default != null && field.default !== '') {
        initial[field.name] = field.default;
      }
    }
    form.setFieldsValue(initial);
  }, [operation.id, artifacts, form, operation.fields]);

  const handleSubmit = async () => {
    try {
      const values = await form.validate();
      onExecute(values as Record<string, unknown>, confirmProd);
    } catch {
      /* validation */
    }
  };

  if (operation.id === 'sign.verify_sample') {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          使用 PDF §2.3 固定示例参数验签，无需填写表单。
        </Typography.Text>
        <Button type="primary" icon={<IconPlayArrow />} loading={executing} onClick={() => onExecute({}, false)}>
          执行验签
        </Button>
      </Space>
    );
  }

  return (
    <Form form={form} layout="vertical" autoComplete="off">
      {operation.requiresArtifacts && operation.requiresArtifacts.length > 0 && (
        <Alert
          type="info"
          content={`建议产物: ${operation.requiresArtifacts.join(', ')}`}
          style={{ marginBottom: 12 }}
        />
      )}
      {operation.fields.map((field) => {
        if (field.type === 'select') {
          return (
            <Form.Item
              key={field.name}
              label={field.label}
              field={field.name}
              rules={field.required ? [{ required: true, message: '必填' }] : undefined}
              extra={field.help}
            >
              <Select options={field.options} allowClear={!field.required} />
            </Form.Item>
          );
        }
        if (field.type === 'int' || field.type === 'cents') {
          return (
            <Form.Item
              key={field.name}
              label={field.label}
              field={field.name}
              rules={field.required ? [{ required: true, message: '必填' }] : undefined}
              extra={field.help}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          );
        }
        return (
          <Form.Item
            key={field.name}
            label={field.label}
            field={field.name}
            rules={field.required ? [{ required: true, message: '必填' }] : undefined}
            extra={field.help || (field.bindArtifact ? `绑定产物: ${field.bindArtifact}` : undefined)}
          >
            <Input placeholder={field.placeholder} allowClear={!field.required} />
          </Form.Item>
        );
      })}
      {isProduction && isWriteOp && (
        <Form.Item>
          <Checkbox checked={confirmProd} onChange={setConfirmProd}>
            我确认在生产环境执行写操作
          </Checkbox>
        </Form.Item>
      )}
      <Button type="primary" icon={<IconPlayArrow />} loading={executing} onClick={handleSubmit}>
        执行
      </Button>
    </Form>
  );
}

export default function SessionWorkbenchView() {
  const { sessionId = '' } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [session, setSession] = useState<LabSession | null>(null);
  const [selectedOpId, setSelectedOpId] = useState<string>('virtualcard.listcardbins');
  const [executing, setExecuting] = useState(false);
  const [lastRun, setLastRun] = useState<LabRun | null>(null);
  const [runs, setRuns] = useState<LabRun[]>([]);
  const [webhooks, setWebhooks] = useState<LabWebhookItem[]>([]);
  const [artifactsJson, setArtifactsJson] = useState('');
  const [runDrawer, setRunDrawer] = useState<LabRun | null>(null);
  const [loading, setLoading] = useState(true);

  const selectedOp = useMemo(
    () => catalog?.operations.find((o) => o.id === selectedOpId),
    [catalog, selectedOpId],
  );

  const loadAll = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const [cat, sess, runList, wh] = await Promise.all([
        upstreamLabApi.getCatalog(),
        upstreamLabApi.getSession(sessionId),
        upstreamLabApi.listRuns(sessionId, 1, 50),
        upstreamLabApi.listWebhooks(sessionId, { pageSize: 20 }),
      ]);
      setCatalog(cat);
      setSession(sess);
      setArtifactsJson(JSON.stringify(sess.artifacts || {}, null, 2));
      setRuns(runList.items);
      setWebhooks(wh.items);
      if (cat.operations.length && !cat.operations.find((o) => o.id === selectedOpId)) {
        setSelectedOpId(cat.operations[0].id);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedOpId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleExecute = async (params: Record<string, unknown>, confirm: boolean) => {
    if (!sessionId || !selectedOpId) return;
    setExecuting(true);
    try {
      const run = await upstreamLabApi.executeRun(sessionId, {
        operationId: selectedOpId,
        params,
        mergeArtifacts: true,
        confirm,
      });
      setLastRun(run);
      setSession((s) => (s ? { ...s, artifacts: run.artifacts } : s));
      setArtifactsJson(JSON.stringify(run.artifacts || {}, null, 2));
      setRuns((prev) => [run, ...prev]);
      if (run.httpResult === 'success') {
        Message.success('调用完成');
      } else {
        Message.warning(`调用结束: ${run.httpResult}`);
      }
      const wh = await upstreamLabApi.listWebhooks(sessionId, { pageSize: 20 });
      setWebhooks(wh.items);
    } finally {
      setExecuting(false);
    }
  };

  const saveArtifacts = async () => {
    try {
      const parsed = JSON.parse(artifactsJson) as Record<string, unknown>;
      const updated = await upstreamLabApi.patchArtifacts(sessionId, parsed, false);
      setSession(updated);
      Message.success('产物已保存');
    } catch {
      Message.error('JSON 格式无效');
    }
  };

  if (loading && !session) {
    return <div style={{ padding: 24 }}>加载中…</div>;
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size="medium" style={{ width: '100%' }}>
        <Space align="center">
          <Button icon={<IconLeft />} onClick={() => navigate('/upstream-lab')}>
            返回列表
          </Button>
          <Typography.Title heading={5} style={{ margin: 0 }}>
            {session?.name}
          </Typography.Title>
          {catalog && (
            <Tag color={catalog.environment.useMock ? 'green' : 'orange'}>
              {catalog.environment.useMock ? 'Mock' : catalog.environment.newgenBaseUrl}
            </Tag>
          )}
          <Button icon={<IconRefresh />} size="small" onClick={loadAll}>
            刷新
          </Button>
        </Space>

        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 280px', gap: 12, alignItems: 'start' }}>
          <Card title="PDF 接口" size="small" style={{ maxHeight: '70vh', overflow: 'auto' }}>
            {catalog?.operations.map((op) => (
              <div
                key={op.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedOpId(op.id)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedOpId(op.id)}
                style={{
                  padding: '8px 10px',
                  cursor: 'pointer',
                  borderRadius: 4,
                  background: selectedOpId === op.id ? 'var(--color-primary-light-1)' : undefined,
                  marginBottom: 4,
                }}
              >
                <Typography.Text bold>{op.pdfSection}</Typography.Text>
                <div>{op.title}</div>
                {op.devOnly && <Tag size="small" color="blue">dev-only</Tag>}
              </div>
            ))}
          </Card>

          <Card
            title={selectedOp ? `${selectedOp.pdfSection} ${selectedOp.title}` : '选择接口'}
            size="small"
          >
            {selectedOp && catalog && (
              <OperationForm
                operation={selectedOp}
                artifacts={session?.artifacts || {}}
                isProduction={!catalog.environment.isDev}
                onExecute={handleExecute}
                executing={executing}
              />
            )}
            {lastRun && (
              <Collapse style={{ marginTop: 16 }} defaultActiveKey={['response']}>
                <Collapse.Item header="最近一次结果" name="response">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Tag color={HTTP_RESULT_COLOR[lastRun.httpResult] ?? 'gray'}>{lastRun.httpResult}</Tag>
                    {lastRun.warnings.map((w) => (
                      <Alert key={w} type="warning" content={w} />
                    ))}
                    {lastRun.signPreview && (
                      <Typography.Paragraph copyable>
                        签名预览: {lastRun.signPreview.signStringPreview}
                      </Typography.Paragraph>
                    )}
                    {Object.keys(lastRun.extracted).length > 0 && (
                      <Typography.Text>新产物: {JSON.stringify(lastRun.extracted)}</Typography.Text>
                    )}
                    <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 240 }}>
                      {JSON.stringify(lastRun.responsePayload, null, 2)}
                    </pre>
                  </Space>
                </Collapse.Item>
              </Collapse>
            )}
          </Card>

          <Card title="会话产物" size="small">
            <Input.TextArea
              value={artifactsJson}
              onChange={setArtifactsJson}
              autoSize={{ minRows: 12, maxRows: 20 }}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <Button type="outline" size="small" style={{ marginTop: 8 }} onClick={saveArtifacts}>
              保存产物
            </Button>
          </Card>
        </div>

        <Card title="调用历史" size="small">
          <Table
            rowKey="id"
            data={runs}
            pagination={false}
            columns={[
              {
                title: '时间',
                dataIndex: 'createdAt',
                width: 160,
                render: (v: string) => new Date(v).toLocaleString(),
              },
              { title: '操作', dataIndex: 'operationId', width: 180 },
              {
                title: '结果',
                dataIndex: 'httpResult',
                width: 100,
                render: (v: string) => <Tag color={HTTP_RESULT_COLOR[v] ?? 'gray'}>{v}</Tag>,
              },
              { title: '耗时(ms)', dataIndex: 'durationMs', width: 90 },
              {
                title: '',
                width: 80,
                render: (_, row) => (
                  <Button type="text" size="small" onClick={() => setRunDrawer(row)}>
                    详情
                  </Button>
                ),
              },
            ]}
          />
        </Card>

        <Card title="Webhook 观测（§25）" size="small">
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            按会话产物中的 outTradeNoOpen / upstreamCardId 过滤；开卡后等待上游 POST 到此收件箱。
          </Typography.Text>
          <Table
            rowKey="id"
            data={webhooks}
            pagination={false}
            columns={[
              { title: '事件', dataIndex: 'event', width: 120 },
              { title: 'out_trade_no', dataIndex: 'outTradeNo' },
              { title: 'card_id', dataIndex: 'upstreamCardId' },
              {
                title: '签名',
                width: 80,
                render: (_, row) => (
                  <Tag color={row.signValid ? 'green' : 'red'}>{row.signValid ? '有效' : '无效'}</Tag>
                ),
              },
              {
                title: '处理',
                width: 80,
                render: (_, row) => (
                  <Tag color={row.processed ? 'green' : 'blue'}>{row.processed ? '已处理' : '待处理'}</Tag>
                ),
              },
              {
                title: '时间',
                dataIndex: 'receivedAt',
                width: 160,
                render: (v: string) => new Date(v).toLocaleString(),
              },
            ]}
          />
          <Link to="/webhooks" style={{ marginTop: 8, display: 'inline-block' }}>
            打开完整 Webhook 收件箱 →
          </Link>
        </Card>
      </Space>

      <Drawer
        width={560}
        title="调用详情"
        visible={!!runDrawer}
        onCancel={() => setRunDrawer(null)}
        footer={null}
      >
        {runDrawer && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>Request</Typography.Text>
            <pre style={{ fontSize: 12, overflow: 'auto' }}>
              {JSON.stringify(runDrawer.requestPayload, null, 2)}
            </pre>
            <Typography.Text>Response</Typography.Text>
            <pre style={{ fontSize: 12, overflow: 'auto' }}>
              {JSON.stringify(runDrawer.responsePayload, null, 2)}
            </pre>
          </Space>
        )}
      </Drawer>
    </div>
  );
}
